import { useState } from 'react';
import { Icon } from '@/components/Icon';
import { showToast } from '@/lib/toast';
import { TARIFF_LABEL, formatBalance } from '@/lib/clientFormat';
import { useOpenExternalUrl } from '@/hooks/useOpenExternalUrl';
import { fetchClientMt1LicensesUrl, updateClientBalance, type ClientDetail, type UpdateClientBalanceResult } from '@/api/clients';

/**
 * Баланс у.е. – реально выдать/списать с комментарием (BalanceService, см.
 * докблок CustomerController::updateBalance() в Laravel). Лицензии – только
 * просмотр/переход, у нас нет данных о местах/стоимости тарифа для карточки
 * (см. исходное ТЗ на страницу – "Никаких стоимостей тарифа... этого нет").
 */
export function BalancePanel({
  balance,
  onBalanceChange,
  accountId,
  licensesDisabled,
}: {
  balance: ClientDetail['balance'];
  onBalanceChange: (balance: ClientDetail['balance']) => void;
  accountId: number;
  licensesDisabled: boolean;
}) {
  const [formAction, setFormAction] = useState<'add' | 'subtract' | null>(null);
  const licenseMode = balance.tariffType === 'license';
  const { loading: loadingLicenses, open: openLicenses } = useOpenExternalUrl(
    () => fetchClientMt1LicensesUrl(accountId),
    'Не удалось открыть управление лицензиями.',
  );

  return (
    <div className="tariff-card">
      <div className="tariff-card__head">
        <div className="tariff-card__icon">
          <Icon name={licenseMode ? 'fileText' : 'psi'} size={18} />
        </div>
        <div>
          <div className="tariff-card__name">
            {TARIFF_LABEL[balance.tariffType] ?? balance.tariffType}
          </div>
          <div className="tariff-card__sub">Тариф</div>
        </div>
      </div>

      {/* Никогда не будет обоих сразу – тариф либо «у.е.» (баланс + выдать/
          списать), либо «лицензии» (ссылка на MT1), это взаимоисключающие тарифы. */}
      {licenseMode ? (
        licensesDisabled ? (
          <div className="tariff-card__empty">
            <Icon name="alertCircle" size={15} />
            <span>Управление лицензиями недоступно – кабинет отключён.</span>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn--secondary btn--block btn--regular"
            onClick={openLicenses}
            disabled={loadingLicenses}
          >
            {loadingLicenses ? 'Открываем…' : 'Управление лицензиями и подключенными тестами'}
            <Icon name="externalLink" size={14} />
          </button>
        )
      ) : (
        <>
          <div className="tariff-card__stat-row">
            <span className="tariff-card__stat">{balance.value.toLocaleString('ru-RU')}</span>
            <span className="tariff-card__stat-unit">у.е.</span>
            <span className="tariff-card__available">
              Доступно <b>{formatBalance(balance.availableBalance)}</b>
            </span>
          </div>

          {formAction ? (
            <BalanceActionForm
              accountId={accountId}
              action={formAction}
              onCancel={() => setFormAction(null)}
              onSuccess={(result) => {
                onBalanceChange({
                  ...balance,
                  value: result.value,
                  availableBalance: result.availableBalance,
                });
                setFormAction(null);
              }}
            />
          ) : (
            <div className="tariff-card__btn-row">
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setFormAction('add')}
              >
                <Icon name="psi" size={14} /> Выдать
              </button>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setFormAction('subtract')}
              >
                <Icon name="psi" size={14} /> Списать
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BalanceActionForm({
  accountId,
  action,
  onCancel,
  onSuccess,
}: {
  accountId: number;
  action: 'add' | 'subtract';
  onCancel: () => void;
  onSuccess: (result: UpdateClientBalanceResult) => void;
}) {
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) {
      setError('Укажите сумму больше нуля.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await updateClientBalance(accountId, {
        action,
        amount: n,
        comment: comment.trim() || undefined,
      });
      onSuccess(result);
      showToast(action === 'add' ? `Выдано ${formatBalance(n)}` : `Списано ${formatBalance(n)}`);
    } catch {
      setError(
        action === 'add'
          ? 'Не удалось выдать у.е.'
          : 'Не удалось списать у.е. – проверьте остаток.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="balance-form">
      <label className="balance-form__field">
        <span>Сумма, у.е.</span>
        <input
          type="number"
          min={1}
          autoFocus
          value={amount}
          disabled={submitting}
          onChange={(e) => setAmount(e.target.value)}
        />
      </label>
      <label className="balance-form__field">
        <span>Комментарий к операции</span>
        <textarea
          rows={2}
          value={comment}
          disabled={submitting}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Необязательно"
        />
      </label>
      {error && <p className="field-error">{error}</p>}
      <div className="balance-form__actions">
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={onCancel}
          disabled={submitting}
        >
          Отмена
        </button>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? 'Сохранение…' : action === 'add' ? 'Выдать' : 'Списать'}
        </button>
      </div>
    </div>
  );
}
